import React from 'react';

export type QuestionState = 'pending' | 'current' | 'correct' | 'incorrect';

export interface MasteryQuestion {
  state: QuestionState;
}

export type LessonMode = 'story' | 'practice';

interface LessonHeaderProps {
  progress?: number;
  masteryQuestions?: MasteryQuestion[];
  streakCount?: number;
  onClose?: () => void;
  darkMode?: boolean;
  mode?: LessonMode;
}

const LessonHeader: React.FC<LessonHeaderProps> = ({
  progress = 0,
  masteryQuestions = [],
  streakCount = 0,
  onClose,
  darkMode = true,
  mode = 'story',
}) => {
  const progressPercent = Math.max(0, Math.min(1, progress)) * 100;
  const streakActive = streakCount > 0;
  const hasQuestions = masteryQuestions.length > 0;
  const isPracticeMode = mode === 'practice';

  const bgColor = darkMode ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF';
  const borderColor = darkMode ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb';
  const textColor = darkMode ? '#94a3b8' : '#6b7280';
  const textColorHover = darkMode ? '#e2e8f0' : '#1f2937';

  return (
    <div
      className="flex items-center w-full px-6 py-5"
      style={{
        background: bgColor,
        borderBottom: `1px solid ${borderColor}`,
      }}
    >
      {/* Left: Close button */}
      <button
        className="flex items-center justify-center w-10 h-10 transition-colors"
        style={{ color: textColor }}
        onMouseEnter={(e) => e.currentTarget.style.color = textColorHover}
        onMouseLeave={(e) => e.currentTarget.style.color = textColor}
        onClick={onClose}
      >
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>

      {/* Center: Progress bar and question bubbles */}
      <div className="flex-1 flex items-center gap-4 px-10">
        {/* Progress bar */}
        {!hasQuestions && (
          <div
            className="flex-1 h-4 rounded-full overflow-hidden"
            style={{ background: darkMode ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb' }}
          >
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}

        {/* Mastery question pills */}
        {hasQuestions && (
          <div className={`flex items-center mx-auto ${isPracticeMode ? 'gap-1' : 'gap-2.5'}`}>
            {masteryQuestions.map((question, index) => {
              let pillColor = darkMode ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db';
              if (question.state === 'current') pillColor = '#3b82f6';
              else if (question.state === 'correct') pillColor = '#10b981';
              else if (question.state === 'incorrect') pillColor = '#f59e0b';

              // Smaller pills for practice mode to fit more
              const pillWidth = isPracticeMode ? 'w-5' : 'w-10';
              const pillHeight = isPracticeMode ? 'h-2' : 'h-3';

              return (
                <div
                  key={index}
                  className={`${pillWidth} ${pillHeight} rounded-full transition-colors`}
                  style={{ background: pillColor }}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Right: Streak fire */}
      <div className="flex items-center gap-1">
        <span
          className="text-base font-bold transition-colors duration-200"
          style={{ color: streakActive ? '#f97316' : textColor }}
        >
          {streakCount}
        </span>
        <svg
          className="w-6 h-6 transition-colors duration-200"
          style={{ color: streakActive ? '#f97316' : textColor }}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 23c-3.866 0-7-3.134-7-7 0-2.551 1.369-4.787 3.455-6.015C8.18 9.793 8 9.418 8 9c0-.552.448-1 1-1 .379 0 .725.214.894.553C10.553 7.612 11.25 6.5 12 5.5c.75 1 1.447 2.112 2.106 3.053.169-.339.515-.553.894-.553.552 0 1 .448 1 1 0 .418-.18.793-.455.985C17.631 11.213 19 13.449 19 16c0 3.866-3.134 7-7 7zm0-12c-1.5 2-3 4-3 5.5 0 1.933 1.567 3.5 3.5 3.5s3.5-1.567 3.5-3.5c0-1.5-1.5-3.5-3-5.5-.5.667-1 1.333-1 2z"/>
        </svg>
      </div>
    </div>
  );
};

export default LessonHeader;
